import { AccountService } from '@app/services/account.service';
import { environment } from '@environments/environment';
import { Injectable } from '@angular/core';
import { SharedSessionAuthService } from '@app/services/shared-session-auth.service';

import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';

import { catchError, first, from, mergeMap, Observable, switchMap, throwError } from 'rxjs';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private accountService: AccountService,
    private sharedAuth: SharedSessionAuthService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Shared-session endpoints use a separate auth scheme; do not touch them
    // (no Authorization header, no logout on stale QR tokens).
    const isSharedSessionRequest = request.url.indexOf('/v1/shared-sessions') !== -1;
    if (isSharedSessionRequest) {
      return next.handle(request);
    }

    // Shared-session (QR / soundshape) guests carry their JWT via SharedSessionAuthService.
    // Attach it to speaksee API requests and renew on 401.
    if (this.sharedAuth.isActive()) {
      const isApiUrl =
        request.url.startsWith(environment.speaksee.apiUrl) ||
        request.url.startsWith(environment.speaksee.authUrl);
      if (!isApiUrl) return next.handle(request);

      const withToken = (req: HttpRequest<any>) => {
        const token = this.sharedAuth.getAccessToken();
        return token
          ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) // eslint-disable-line
          : req;
      };

      return next.handle(withToken(request)).pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 401) {
            // Renew once, then retry with the fresh token (withToken re-reads it).
            return from(this.sharedAuth.ensureValidToken()).pipe(
              switchMap((ok) =>
                ok ? next.handle(withToken(request)) : throwError(() => err)
              )
            );
          }
          return throwError(() => err);
        })
      );
    }

    // Add user_type: "anonymous" to login requests for QR users
    const isLoginRequest = request.url.indexOf('/auth/login') !== -1;
    if (isLoginRequest) {
      const authMode = sessionStorage.getItem('auth_mode');
      if (authMode === 'qr') {
        // Clone the request and add user_type to the body
        const modifiedBody = {
          ...request.body,
          user_type: 'anonymous'
        };
        request = request.clone({
          body: modifiedBody
        });
      }
    }

    // add auth header with jwt if user is logged in and request is to the api url
    const user = this.accountService.userValue;
    const isLoggedIn = user && user.token;
    const isApiUrl =
      request.url.startsWith(environment.speaksee.apiUrl) ||
      request.url.startsWith(environment.speaksee.authUrl);
    const isNotLogin = request.url.indexOf('/auth/login') === -1;
    const isNotRefresh = request.url.indexOf('/auth/refresh') === -1;

    if (isLoggedIn && isApiUrl && isNotLogin && isNotRefresh && user?.token) {
      const currentTime = new Date().getTime();

      // If refresh token expired logout and return error
      if (user.token.refreshExpireAt < currentTime) {
        return from(this.accountService.logout()).pipe(
          switchMap(() => throwError(() => new Error('Refresh token expired')))
        );
      }

      // If refresh token still valid retrieve new access tokens
      if (user.token.accessExpireAt < currentTime) {
        return this.accountService.refresh().pipe(
          first(),
          catchError((e) => throwError(() => e)),
          mergeMap((response) => {
            request = request.clone({
              setHeaders: {
                Authorization: `Bearer ${response.access_token}`, // eslint-disable-line
              },
            });
            return next.handle(request);
          })
        );
      }

      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${user?.token?.accessToken}`, // eslint-disable-line
        },
      });
    }

    return next.handle(request);
  }
}
